import { useState, useEffect, useRef, useCallback } from "react";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { signInWithCustomToken, signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "@/lib/firebase";
import { isExpectedInviteRedeemError, redeemInvite } from "@/features/auth/lib/invite";
import { exchangeFirebaseToken } from "@/features/auth/lib/exchange";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { getDefaultRoute } from "@/features/auth/lib/page-routes";
import { SetPasswordForm } from "@/features/auth/components/SetPasswordForm";
import { AdgeLogo, Card, CardContent, CardDescription, CardHeader, CardTitle, Button } from "@nexus/ui";
import { Sentry } from "@/lib/sentry";

export const Route = createFileRoute("/auth/invite")({
  validateSearch: (search: Record<string, unknown>) => ({
    token: search.token != null ? String(search.token) : "",
  }),
  component: InvitePage,
});

type InviteStep = "redeeming" | "set-password" | "exchanging" | "error";

/** Deterministic onboarding progress sweep (kept within a 4–6s window). */
const PROGRESS_DURATION_MS = 5000;

/**
 * Onboarding loader for the redeem/exchange wait. A single progress bar sweeps
 * from empty to full over {@link PROGRESS_DURATION_MS}; the flow only advances
 * once it reaches 100% (`onProgressComplete`), so the first product moment reads
 * as a considered setup rather than a spinner flash.
 */
function InviteSetupLoader({
  exchanging,
  email,
  onProgressComplete,
}: {
  exchanging: boolean;
  email?: string;
  onProgressComplete?: () => void;
}) {
  const { t } = useTranslation();
  // Defer the bar's first fill to the frame after mount so it animates in from
  // empty rather than snapping straight to full.
  const [filled, setFilled] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setFilled(true));
    return () => cancelAnimationFrame(id);
  }, []);

  // Advance once the sweep completes. A timer (not transitionend) drives this so
  // it still fires under prefers-reduced-motion, where the bar snaps to full.
  useEffect(() => {
    if (exchanging) return;
    const id = setTimeout(() => onProgressComplete?.(), PROGRESS_DURATION_MS);
    return () => clearTimeout(id);
  }, [exchanging, onProgressComplete]);

  const message = exchanging ? t("auth.signingIn") : t("auth.setupStepAccount");
  const ratio = exchanging || filled ? 1 : 0.05;
  const barDurationMs = exchanging ? 600 : PROGRESS_DURATION_MS;
  // Linear reads as honest, steady progress across the determinate sweep.
  const barEasing = exchanging ? "ease-out" : "ease-linear";

  return (
    <div className="flex min-h-svh items-center justify-center bg-[var(--background)] p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="space-y-4 text-center">
          <div className="flex justify-center">
            <AdgeLogo aria-label={t("auth.brandName")} />
          </div>
          <div className="space-y-1.5">
            <CardTitle className="text-2xl font-semibold tracking-tight">
              {t("auth.inviteWelcomeTitle")}
            </CardTitle>
            <CardDescription className="text-sm">
              <span
                key={message}
                className="inline-block animate-in fade-in-0 slide-in-from-bottom-1 duration-500 motion-reduce:animate-none"
              >
                {message}
              </span>
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div
            className="h-1.5 w-full overflow-hidden rounded-full bg-[var(--muted)]"
            role="progressbar"
            aria-label={t("common.loading")}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(ratio * 100)}
          >
            <div
              className={`h-full w-full origin-left rounded-full bg-[var(--primary)] ${barEasing} transition-transform motion-reduce:transition-none`}
              style={{ transform: `scaleX(${ratio})`, transitionDuration: `${barDurationMs}ms` }}
            />
          </div>
          {email && (
            <p className="text-center text-xs text-[var(--muted-foreground)] break-all">{email}</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function InvitePage() {
  const { t } = useTranslation();
  useDocumentTitle(t("page.title.invite"));
  const navigate = useNavigate();
  const { token } = useSearch({ from: "/auth/invite" });
  const lastVisitedRoute = useAuth((s) => s.lastVisitedRoute);

  const [step, setStep] = useState<InviteStep>("redeeming");
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState<string>("");
  // The redeem result is held until the onboarding progress bar reaches 100%;
  // only then do we reveal the password form (or run the exchange).
  const [redeemReady, setRedeemReady] = useState<{ requiresPassword: boolean } | null>(null);
  const [progressComplete, setProgressComplete] = useState(false);
  const redeemedRef = useRef(false);

  const handleProgressComplete = useCallback(() => setProgressComplete(true), []);

  useEffect(() => {
    if (!token) {
      setError("No invite token provided.");
      setStep("error");
      return;
    }
    if (redeemedRef.current) return;
    redeemedRef.current = true;
    void handleRedeem(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Gate the transition on both the redeem completing and the progress sweep.
  useEffect(() => {
    if (!redeemReady || !progressComplete) return;
    if (redeemReady.requiresPassword) {
      setStep("set-password");
    } else {
      void exchangeAndRedirect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redeemReady, progressComplete]);

  async function handleRedeem(inviteToken: string) {
    try {
      setStep("redeeming");

      // 1. Redeem invite → get Firebase custom token
      const { customToken, email: inviteEmail, requiresPassword } = await redeemInvite(inviteToken);
      setEmail(inviteEmail);

      // 2. Sign in to Firebase with the custom token
      await signInWithCustomToken(auth, customToken);

      // 3. Hand off to the progress sweep — the gate effect advances the flow
      // once the bar hits 100%.
      setRedeemReady({ requiresPassword });
    } catch (err) {
      if (!isExpectedInviteRedeemError(err)) {
        Sentry.captureException(err, { tags: { flow: "invite-redeem" } });
      }
      setError(t("auth.inviteExpired"));
      setStep("error");
    }
  }

  async function handlePasswordSet(password: string) {
    // Re-authenticate after password set to get a non-revoked token
    await signInWithEmailAndPassword(auth, email, password);
    await exchangeAndRedirect();
  }

  async function exchangeAndRedirect() {
    setStep("exchanging");
    try {
      const user = auth.currentUser;
      if (!user) throw new Error("Not authenticated");
      const firebaseToken = await user.getIdToken(true);
      const { tenants } = await exchangeFirebaseToken(firebaseToken);
      const defaultRoute = getDefaultRoute(tenants, lastVisitedRoute);
      void navigate({ to: defaultRoute ?? "/login" });
    } catch (err) {
      Sentry.captureException(err, { tags: { flow: "invite-exchange" } });
      setError(t("auth.inviteExpired"));
      setStep("error");
    }
  }

  // Set password step
  if (step === "set-password") {
    return <SetPasswordForm onComplete={handlePasswordSet} email={email} />;
  }

  // Error state
  if (step === "error") {
    return (
      <div className="flex min-h-svh items-center justify-center bg-[var(--background)] p-4">
        <Card className="w-full max-w-sm">
          <CardHeader className="space-y-5 text-center">
            <div className="flex justify-center">
              <AdgeLogo aria-label={t("auth.brandName")} />
            </div>
            <div className="space-y-2">
              <CardTitle className="text-2xl font-semibold tracking-tight">{t("auth.inviteFailed")}</CardTitle>
              <CardDescription className="text-balance leading-relaxed">{error}</CardDescription>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button className="w-full" onClick={() => void navigate({ to: "/login" })}>
              {t("errors.backToLogin")}
            </Button>
            <p className="text-center text-xs text-[var(--muted-foreground)]">
              {t("auth.inviteHelpContactAdmin")}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Redeeming / exchanging — onboarding progress loader
  return (
    <InviteSetupLoader
      exchanging={step === "exchanging"}
      email={email}
      onProgressComplete={handleProgressComplete}
    />
  );
}
